import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { chambersApi, clientsApi, mattersApi } from "@/lib/api";
import { Briefcase, Plus, MagnifyingGlass, X, Buildings } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

const COURT_OPTIONS = [
    "Supreme Court of India",
    "Delhi High Court",
    "Delhi District Court — Patiala House",
    "Delhi District Court — Tis Hazari",
    "Delhi District Court — Karkardooma",
    "Delhi District Court — Saket",
    "Delhi District Court — Rohini",
    "Delhi District Court — Dwarka",
    "Tribunal",
    "Other",
];

const STAGE_OPTIONS = [
    "Pleadings",
    "Admission",
    "Evidence",
    "Arguments",
    "Reserved for judgment",
    "Appeal",
    "Interim",
    "Execution",
    "Closed",
];

const PRACTICE_AREAS = [
    "Civil",
    "Criminal",
    "Commercial",
    "Constitutional",
    "Family",
    "Labour",
    "Tax",
    "IP / Trademark",
    "Arbitration",
    "Writs",
    "Consumer",
    "Other",
];

export default function Matters() {
    const [matters, setMatters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [sp, setSp] = useSearchParams();
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const data = await mattersApi.list();
            setMatters(data);
        } catch {
            toast.error("Failed to load matters");
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        if (sp.get("new") === "1") {
            setOpen(true);
            sp.delete("new");
            setSp(sp, { replace: true });
        }
    }, [sp, setSp]);

    const filtered = matters.filter((m) => {
        if (!q.trim()) return true;
        const s = q.toLowerCase();
        return (
            m.title?.toLowerCase().includes(s) ||
            m.client_name?.toLowerCase().includes(s) ||
            m.opposing_party?.toLowerCase().includes(s) ||
            m.case_number?.toLowerCase().includes(s) ||
            m.court?.toLowerCase().includes(s)
        );
    });

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="matters-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Workspace</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Matters</h1>
                    <HelpButton topic="matters" testid="help-matters" />
                </div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-matter-btn"><Plus size={16} /> New matter</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle className="font-serif">Create a new matter</DialogTitle>
                        </DialogHeader>
                        <MatterForm
                            onDone={(m) => {
                                setOpen(false);
                                setMatters((prev) => [m, ...prev]);
                                navigate(`/matters/${m.id}`);
                            }}
                        />
                    </DialogContent>
                </Dialog>
            </div>

            <div className="relative mb-4 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                    data-testid="matters-search"
                    placeholder="Search title, client, case number, court…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="pl-9"
                />
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : !filtered.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-center">
                    <Briefcase size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                    <div className="font-serif text-xl mb-1">No matters yet</div>
                    <div className="text-sm text-muted-foreground">Create your first matter to get started.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map((m) => (
                        <Link
                            key={m.id}
                            to={`/matters/${m.id}`}
                            data-testid={`matter-card-${m.id}`}
                            className="border border-border rounded-md bg-white p-5 hover:shadow-sm hover:border-primary/40 transition-all block"
                        >
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="font-serif text-xl leading-tight line-clamp-2">{m.title}</div>
                                {m.practice_area && (
                                    <span className="chip chip-doc shrink-0">{m.practice_area}</span>
                                )}
                            </div>
                            <div className="text-sm text-foreground/80 mb-3 line-clamp-1">
                                {m.client_name || "—"} <span className="text-muted-foreground">v.</span>{" "}
                                {m.opposing_party || "—"}
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center justify-between">
                                <span className="truncate">{m.court || "Court TBD"}</span>
                                <span className="font-mono shrink-0 ml-2">{m.case_number || "—"}</span>
                            </div>
                            {m.hearing_date && (
                                <div className="mt-3 text-xs">
                                    <span className="chip chip-external">Hearing: {m.hearing_date}</span>
                                </div>
                            )}
                            {m.chamber_id && (
                                <div className="mt-2 text-xs">
                                    <span className="chip chip-ai inline-flex items-center gap-1"><Buildings size={11} weight="duotone" /> Shared</span>
                                </div>
                            )}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

export function MatterForm({ initial, onDone }) {
    const [v, setV] = useState(
        initial || {
            title: "",
            client_id: null,
            client_name: "",
            opposing_party: "",
            court: "Delhi High Court",
            case_number: "",
            stage: "Pleadings",
            hearing_date: "",
            practice_area: "Civil",
            notes: "",
            tags: [],
            chamber_id: null,
        },
    );
    const [saving, setSaving] = useState(false);
    const [tagInput, setTagInput] = useState("");
    const [chambers, setChambers] = useState([]);
    const [clients, setClients] = useState([]);

    useEffect(() => {
        chambersApi.list().then(setChambers).catch(() => {});
        clientsApi.list().then(setClients).catch(() => {});
    }, []);

    const save = async () => {
        if (!v.title?.trim()) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...v, chamber_id: v.chamber_id && v.chamber_id !== "none" ? v.chamber_id : null };
            const res = initial ? await mattersApi.update(initial.id, payload) : await mattersApi.create(payload);
            toast.success(initial ? "Matter updated" : "Matter created");
            onDone?.(res);
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Failed to save");
        }
        setSaving(false);
    };

    return (
        <div className="space-y-4" data-testid="matter-form">
            <Field label="Title *">
                <Input data-testid="matter-title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Client">
                    <Select
                        value={v.client_id || "free_text"}
                        onValueChange={(val) => {
                            if (val === "free_text") {
                                setV({ ...v, client_id: null });
                            } else {
                                const c = clients.find((cl) => cl.id === val);
                                setV({ ...v, client_id: val, client_name: c?.name || v.client_name });
                            }
                        }}
                    >
                        <SelectTrigger data-testid="matter-client-select"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="free_text">Free-text (no CRM record)</SelectItem>
                            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_id_human} — {c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {!v.client_id && (
                        <Input
                            data-testid="matter-client"
                            value={v.client_name}
                            onChange={(e) => setV({ ...v, client_name: e.target.value })}
                            placeholder="Client name (typed, not linked to CRM)"
                            className="mt-2"
                        />
                    )}
                </Field>
                <Field label="Opposing party">
                    <Input data-testid="matter-opposing" value={v.opposing_party} onChange={(e) => setV({ ...v, opposing_party: e.target.value })} />
                </Field>
                <Field label="Court">
                    <Select value={v.court} onValueChange={(val) => setV({ ...v, court: val })}>
                        <SelectTrigger data-testid="matter-court"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {COURT_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Case number">
                    <Input data-testid="matter-case-number" value={v.case_number} onChange={(e) => setV({ ...v, case_number: e.target.value })} />
                </Field>
                <Field label="Stage">
                    <Select value={v.stage} onValueChange={(val) => setV({ ...v, stage: val })}>
                        <SelectTrigger data-testid="matter-stage"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAGE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Practice area">
                    <Select value={v.practice_area} onValueChange={(val) => setV({ ...v, practice_area: val })}>
                        <SelectTrigger data-testid="matter-practice"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRACTICE_AREAS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Next hearing date">
                    <Input type="date" data-testid="matter-hearing" value={v.hearing_date || ""} onChange={(e) => setV({ ...v, hearing_date: e.target.value })} />
                </Field>
                <Field label="Chamber (optional)">
                    <Select value={v.chamber_id || "none"} onValueChange={(val) => setV({ ...v, chamber_id: val === "none" ? null : val })}>
                        <SelectTrigger data-testid="matter-chamber"><SelectValue placeholder="Personal" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Personal (private)</SelectItem>
                            {chambers.filter((c) => c.my_role !== "paralegal").map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    <Buildings size={11} weight="duotone" className="inline-block mr-1" /> {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            </div>
            <Field label="Notes">
                <Textarea data-testid="matter-notes" rows={4} value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
            </Field>
            <Field label="Tags">
                <div className="flex flex-wrap items-center gap-2">
                    {v.tags?.map((t) => (
                        <span key={t} className="chip chip-doc">
                            {t}
                            <button onClick={() => setV({ ...v, tags: v.tags.filter((x) => x !== t) })}>
                                <X size={10} />
                            </button>
                        </span>
                    ))}
                    <Input
                        className="w-36"
                        placeholder="add tag, enter"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && tagInput.trim()) {
                                e.preventDefault();
                                setV({ ...v, tags: [...(v.tags || []), tagInput.trim()] });
                                setTagInput("");
                            }
                        }}
                    />
                </div>
            </Field>
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border flex justify-end gap-2 mt-2">
                <Button data-testid="save-matter-btn" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : initial ? "Save changes" : "Create matter"}
                </Button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}
